"use client";

/**
 * `<HeroScene3d>`: orbiting-analyzers 3D hero background (client island).
 *
 * Adapted from the design-kit `landing-3d.js` reference (particle terrain,
 * icosahedron core, 7 orbiting analyzer nodes with HTML labels + inbound
 * data packets, star dust, pointer parallax) with the following changes:
 *
 * - Renders INSIDE the hero section (absolute inset-0, z-0,
 *   pointer-events-none), never position:fixed, never touches any other
 *   element's styles.
 * - No OrbitControls: slow auto-rotation on the root group + a light
 *   pointer parallax only.
 * - Transparent canvas (alpha:true, no scene.background) so the scene
 *   layers over the page's existing navy, no hard rectangle.
 * - three.js is loaded lazily via dynamic `import("three")` AFTER window
 *   load + requestIdleCallback, so it is code-split out of the initial
 *   bundle and LCP stays the existing headline text.
 * - Desktop-only gate: `(pointer: fine) and (min-width: 1024px)` and NOT
 *   `prefers-reduced-motion`; otherwise renders nothing and the hero's
 *   existing static background stays.
 * - IntersectionObserver pauses the RAF loop when the hero is offscreen;
 *   pixelRatio capped at 2; full dispose/cleanup on unmount.
 */

import { useEffect, useRef, useState } from "react";

type ThreeModule = typeof import("three");

/** Brand palette (mirrors src/styles/design-tokens.css). */
const PALETTE = {
  bg: 0x0a0e1a,
  blue: 0x3b82f6,
  blueGlow: 0x60a5fa,
  gold: 0xf59e0b,
  green: 0x10b981,
  red: 0xef4444,
  slate: 0x7e8fa6,
};

/** The 7 Compass analyzers as orbiting nodes. */
const NODES = [
  { name: "FUNDAMENTAL", color: PALETTE.green, r: 6.4, tilt: 0.18, speed: 0.22, phase: 0.0 },
  { name: "VALUATION", color: PALETTE.gold, r: 7.6, tilt: 0.5, speed: -0.17, phase: 0.9 },
  { name: "SMART MONEY", color: PALETTE.blue, r: 5.8, tilt: -0.42, speed: 0.28, phase: 1.8 },
  { name: "NARRATIVE", color: PALETTE.slate, r: 8.2, tilt: 0.3, speed: 0.14, phase: 2.7 },
  { name: "MACRO", color: PALETTE.red, r: 7.0, tilt: -0.6, speed: -0.24, phase: 3.6 },
  { name: "TECHNICAL", color: PALETTE.blueGlow, r: 6.1, tilt: 0.7, speed: 0.19, phase: 4.5 },
  { name: "CATALYST", color: PALETTE.gold, r: 8.0, tilt: -0.22, speed: -0.13, phase: 5.4 },
] as const;

export function HeroScene3d() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Desktop fine-pointer + wide viewport + motion-ok, else render nothing.
    if (!window.matchMedia("(pointer: fine) and (min-width: 1024px)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let cancelled = false;
    let disposeScene: (() => void) | null = null;
    let cancelIdle: (() => void) | null = null;

    const start = () => {
      if (cancelled) return;
      void import("three").then((THREE) => {
        if (cancelled || !hostRef.current) return;
        disposeScene = initScene(THREE, hostRef.current);
        setActive(true);
      });
    };

    // Defer until the browser is idle after full page load so the 3D work
    // never competes with LCP / hydration.
    const scheduleIdle = () => {
      if (cancelled) return;
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      if (typeof w.requestIdleCallback === "function") {
        const id = w.requestIdleCallback(start, { timeout: 3000 });
        cancelIdle = () => w.cancelIdleCallback?.(id);
      } else {
        const id = window.setTimeout(start, 800);
        cancelIdle = () => window.clearTimeout(id);
      }
    };

    if (document.readyState === "complete") {
      scheduleIdle();
    } else {
      window.addEventListener("load", scheduleIdle, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", scheduleIdle);
      cancelIdle?.();
      disposeScene?.();
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {/* Subtle bottom-up navy wash between canvas and hero text so the
          headline keeps AA contrast over the scene (additive only). */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          opacity: active ? 1 : 0,
          transition: "opacity 1.2s ease",
          background:
            "linear-gradient(to top, rgba(10,14,26,0.72) 0%, rgba(10,14,26,0.28) 45%, rgba(10,14,26,0) 75%), linear-gradient(to right, rgba(10,14,26,0.55) 0%, rgba(10,14,26,0.15) 45%, rgba(10,14,26,0) 70%)",
        }}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

/** Builds the scene into `host`; returns a full-cleanup disposer. */
function initScene(THREE: ThreeModule, host: HTMLDivElement): () => void {
  const width = () => Math.max(1, host.clientWidth);
  const height = () => Math.max(1, host.clientHeight);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width(), height());
  renderer.setClearColor(0x000000, 0); // fully transparent, page navy shows through
  const canvas = renderer.domElement;
  canvas.style.position = "absolute";
  canvas.style.inset = "0";
  canvas.style.opacity = "0";
  canvas.style.transition = "opacity 1.2s ease";
  host.appendChild(canvas);
  requestAnimationFrame(() => {
    canvas.style.opacity = "1";
  });

  const scene = new THREE.Scene();
  // No scene.background (transparent). FogExp2 only tints geometry toward
  // the page navy with distance, which composites fine over alpha.
  scene.fog = new THREE.FogExp2(PALETTE.bg, 0.026);

  const camera = new THREE.PerspectiveCamera(48, width() / height(), 0.1, 220);
  camera.position.set(0, 4.6, 21);
  camera.lookAt(0, 0.6, 0);

  scene.add(new THREE.AmbientLight(0x22304e, 1.15));
  const key = new THREE.PointLight(PALETTE.blueGlow, 2.2, 70);
  key.position.set(9, 12, 10);
  scene.add(key);
  const coreLight = new THREE.PointLight(PALETTE.gold, 0.55, 28);
  scene.add(coreLight);

  const root = new THREE.Group();
  // Shift the composition right-of-center so the core sits clear of the
  // left-aligned headline column.
  root.position.x = 5.5;
  scene.add(root);
  coreLight.position.x = 5.5;

  /* Glow sprite factory (canvas radial gradient texture). */
  const glowTextures: import("three").Texture[] = [];
  function makeGlow(colorHex: number, size: number) {
    const cvs = document.createElement("canvas");
    cvs.width = cvs.height = 128;
    const ctx = cvs.getContext("2d")!;
    const col = new THREE.Color(colorHex);
    const r = (col.r * 255) | 0;
    const g = (col.g * 255) | 0;
    const b = (col.b * 255) | 0;
    const grad = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    grad.addColorStop(0, `rgba(${r},${g},${b},0.85)`);
    grad.addColorStop(0.35, `rgba(${r},${g},${b},0.3)`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 128, 128);
    const tex = new THREE.CanvasTexture(cvs);
    glowTextures.push(tex);
    const spr = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: tex,
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
      })
    );
    spr.scale.set(size, size, 1);
    return spr;
  }

  /* Particle terrain (grid floor, gentle waves). */
  const GW = 90;
  const GH = 46;
  const SP = 0.85;
  const tCount = GW * GH;
  const tPos = new Float32Array(tCount * 3);
  let ti = 0;
  for (let gy = 0; gy < GH; gy++) {
    for (let gx = 0; gx < GW; gx++) {
      tPos[ti++] = (gx - GW / 2) * SP;
      tPos[ti++] = -5.2;
      tPos[ti++] = (gy - GH / 2) * SP;
    }
  }
  const terrainGeo = new THREE.BufferGeometry();
  terrainGeo.setAttribute("position", new THREE.BufferAttribute(tPos, 3));
  const terrain = new THREE.Points(
    terrainGeo,
    new THREE.PointsMaterial({
      color: PALETTE.blue,
      size: 0.055,
      transparent: true,
      opacity: 0.55,
      sizeAttenuation: true,
    })
  );
  scene.add(terrain);

  /* Crystalline core. */
  const coreGroup = new THREE.Group();
  coreGroup.position.y = 0.6;
  root.add(coreGroup);

  const coreMat = new THREE.MeshStandardMaterial({
    color: 0x16203a,
    metalness: 0.65,
    roughness: 0.25,
    emissive: PALETTE.blue,
    emissiveIntensity: 0.3,
    flatShading: true,
  });
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(2.0, 0), coreMat);
  coreGroup.add(core);
  const wire = new THREE.Mesh(
    new THREE.IcosahedronGeometry(2.5, 1),
    new THREE.MeshBasicMaterial({
      color: PALETTE.blueGlow,
      wireframe: true,
      transparent: true,
      opacity: 0.15,
    })
  );
  coreGroup.add(wire);
  const ringMat = new THREE.MeshBasicMaterial({
    color: PALETTE.gold,
    transparent: true,
    opacity: 0,
  });
  const ring = new THREE.Mesh(new THREE.TorusGeometry(3.1, 0.04, 12, 120), ringMat);
  ring.rotation.x = Math.PI / 2;
  coreGroup.add(ring);
  coreGroup.add(makeGlow(PALETTE.blueGlow, 8.5));

  /* Label layer (HTML, inside our own host, mono font + muted token). */
  const labelLayer = document.createElement("div");
  labelLayer.style.position = "absolute";
  labelLayer.style.inset = "0";
  labelLayer.style.zIndex = "2";
  labelLayer.style.overflow = "hidden";
  labelLayer.style.pointerEvents = "none";
  host.appendChild(labelLayer);

  /* Analyzer nodes + orbit trails + inbound links/packets + labels. */
  type NodeRec = {
    def: (typeof NODES)[number];
    group: import("three").Group;
    linkGeo: import("three").BufferGeometry;
    packet: import("three").Mesh<
      import("three").SphereGeometry,
      import("three").MeshBasicMaterial
    >;
    label: HTMLDivElement;
    v: import("three").Vector3;
  };
  const nodes: NodeRec[] = [];

  for (const def of NODES) {
    const g = new THREE.Group();
    g.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(0.3, 22, 22),
        new THREE.MeshStandardMaterial({
          color: def.color,
          emissive: def.color,
          emissiveIntensity: 0.9,
          metalness: 0.3,
          roughness: 0.4,
        })
      )
    );
    g.add(makeGlow(def.color, 2.2));
    root.add(g);

    const trail = new THREE.Mesh(
      new THREE.TorusGeometry(def.r, 0.01, 8, 150),
      new THREE.MeshBasicMaterial({ color: def.color, transparent: true, opacity: 0.1 })
    );
    trail.rotation.x = Math.PI / 2 + def.tilt;
    trail.position.y = 0.6;
    root.add(trail);

    const linkGeo = new THREE.BufferGeometry();
    linkGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
    const link = new THREE.Line(
      linkGeo,
      new THREE.LineBasicMaterial({ color: def.color, transparent: true, opacity: 0.2 })
    );
    root.add(link);

    const packet = new THREE.Mesh(
      new THREE.SphereGeometry(0.09, 10, 10),
      new THREE.MeshBasicMaterial({ color: def.color, transparent: true })
    );
    root.add(packet);

    const label = document.createElement("div");
    label.textContent = def.name;
    label.style.position = "absolute";
    label.style.left = "0";
    label.style.top = "0";
    label.style.fontFamily = "var(--font-mono, ui-monospace, Menlo, monospace)";
    label.style.fontSize = "10px";
    label.style.letterSpacing = "0.14em";
    label.style.color = "var(--color-muted, #94A3B8)";
    label.style.opacity = "0";
    label.style.transition = "opacity 0.4s ease";
    label.style.transform = "translate(-50%, -100%)";
    label.style.whiteSpace = "nowrap";
    label.style.textShadow = "0 1px 6px rgba(10,14,26,0.9)";
    labelLayer.appendChild(label);

    nodes.push({ def, group: g, linkGeo, packet, label, v: new THREE.Vector3() });
  }

  /* Star dust. */
  const dCount = 260;
  const dPos = new Float32Array(dCount * 3);
  for (let i = 0; i < dCount; i++) {
    const rr = 14 + Math.random() * 26;
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    dPos[i * 3] = rr * Math.sin(ph) * Math.cos(th);
    dPos[i * 3 + 1] = Math.abs(rr * Math.cos(ph)) * 0.6 - 2;
    dPos[i * 3 + 2] = rr * Math.sin(ph) * Math.sin(th);
  }
  const dustGeo = new THREE.BufferGeometry();
  dustGeo.setAttribute("position", new THREE.BufferAttribute(dPos, 3));
  const dust = new THREE.Points(
    dustGeo,
    new THREE.PointsMaterial({
      color: PALETTE.slate,
      size: 0.05,
      transparent: true,
      opacity: 0.45,
    })
  );
  scene.add(dust);

  /* Pointer parallax (light, no drag, no controls). */
  let mx = 0;
  const onPointerMove = (e: PointerEvent) => {
    mx = e.clientX / window.innerWidth - 0.5;
  };
  window.addEventListener("pointermove", onPointerMove, { passive: true });

  /* Resize with the host, not the window. */
  const onResize = () => {
    camera.aspect = width() / height();
    camera.updateProjectionMatrix();
    renderer.setSize(width(), height());
  };
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(host);

  /* Pause the loop while the hero is offscreen. */
  let visible = true;
  let rafId = 0;
  const io = new IntersectionObserver(
    (entries) => {
      const wasVisible = visible;
      visible = entries[0]?.isIntersecting ?? true;
      if (visible && !wasVisible && !disposed) {
        rafId = requestAnimationFrame(animate);
      }
    },
    { threshold: 0 }
  );
  io.observe(host);

  /* Project a world point into host-relative CSS pixels. */
  const proj = new THREE.Vector3();
  function toScreen(v: import("three").Vector3) {
    proj.copy(v).project(camera);
    return {
      x: (proj.x * 0.5 + 0.5) * width(),
      y: (-proj.y * 0.5 + 0.5) * height(),
      vis: proj.z < 1,
    };
  }

  // Plain timer (THREE.Clock is deprecated in r185). Elapsed time only
  // advances while the loop runs, so an offscreen pause resumes seamlessly.
  let prevMs = performance.now();
  let elapsed = 0;
  let autoRot = 0;
  let parallax = 0;
  let disposed = false;

  function animate() {
    if (disposed || !visible) return;
    rafId = requestAnimationFrame(animate);

    const nowMs = performance.now();
    const dt = Math.min((nowMs - prevMs) / 1000, 0.1);
    prevMs = nowMs;
    elapsed += dt;
    const t = elapsed;

    core.rotation.y = t * 0.16;
    core.rotation.x = t * 0.08;
    wire.rotation.y = -t * 0.11;
    coreMat.emissiveIntensity = 0.28 + Math.sin(t * 1.5) * 0.09;
    coreLight.intensity = 0.5 + Math.sin(t * 1.5) * 0.25;
    ring.rotation.z = t * 0.22;
    ringMat.opacity = Math.min(0.8, t * 0.12);

    // Terrain wave.
    const arr = terrainGeo.attributes.position.array as Float32Array;
    for (let k = 0; k < tCount; k++) {
      const x = arr[k * 3];
      const z = arr[k * 3 + 2];
      arr[k * 3 + 1] =
        -5.2 + Math.sin(x * 0.35 + t * 0.9) * 0.35 + Math.cos(z * 0.3 + t * 0.6) * 0.35;
    }
    terrainGeo.attributes.position.needsUpdate = true;

    // Orbiting analyzer nodes + links + packets + labels.
    for (let j = 0; j < nodes.length; j++) {
      const n = nodes[j];
      const a = n.def;
      const ang = a.phase + t * a.speed;
      const x2 = Math.cos(ang) * a.r;
      const z2 = Math.sin(ang) * a.r;
      const y2 = 0.6 + Math.sin(ang + a.phase) * a.r * Math.sin(a.tilt);
      n.group.position.set(x2, y2, z2);
      n.group.getWorldPosition(n.v);

      const lp = n.linkGeo.attributes.position.array as Float32Array;
      lp[0] = x2;
      lp[1] = y2;
      lp[2] = z2;
      lp[3] = 0;
      lp[4] = 0.6;
      lp[5] = 0;
      n.linkGeo.attributes.position.needsUpdate = true;

      const tr = (t * 0.32 + j * 0.14) % 1;
      n.packet.position.set(x2 * (1 - tr), y2 + (0.6 - y2) * tr, z2 * (1 - tr));
      n.packet.material.opacity = 1 - tr;

      const s = toScreen(n.v);
      n.label.style.left = `${s.x}px`;
      n.label.style.top = `${s.y - 20}px`;
      n.label.style.opacity = s.vis ? "0.9" : "0";
    }

    dust.rotation.y = t * 0.008;

    // Slow auto-rotation + light pointer parallax on the root group.
    autoRot += dt * 0.05;
    parallax += (mx * 0.18 - parallax) * 0.03;
    root.rotation.y = autoRot + parallax;

    renderer.render(scene, camera);
  }
  rafId = requestAnimationFrame(animate);

  /* Full cleanup. */
  return () => {
    disposed = true;
    cancelAnimationFrame(rafId);
    io.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener("pointermove", onPointerMove);

    scene.traverse((obj) => {
      const mesh = obj as import("three").Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const material = (mesh as { material?: unknown }).material;
      const mats = Array.isArray(material) ? material : material ? [material] : [];
      for (const m of mats) {
        const mat = m as import("three").Material & { map?: import("three").Texture | null };
        if (mat.map) mat.map.dispose();
        mat.dispose();
      }
    });
    for (const tex of glowTextures) tex.dispose();
    renderer.dispose();
    canvas.remove();
    labelLayer.remove();
  };
}
